import {
	BaseEntityNames,
	BUILT_IN_BASE_ENTITY_IDS,
	checkHasBaseEntity,
	EntityType,
	EntityWithValueState,
	getBaseBuiltInEntities,
	getExternalIntegrationNameFromBuiltInBaseName,
	inferDataType,
	LiteralValueState,
	PrimitiveTypes,
	PropertyState,
	ValueDescriptorState,
	DefinitionEntityState,
	BuiltInBaseEntityState,
	EntityState,
	PrimitiveEntityState,
	getFirstBaseEntityWithOwnBase,
	ENTITY_WITH_DEFAULT_VALUE_TYPES,
	EntityWithDefaultValueState,
	DataTypeState,
	FunctionDeclarationState
} from '@elyx-code/project-logic-tree';
import NumberDataTypeBlackIcon from '../../assets/datatype-icons/black/number.svg';
import ListDataTypeBlackIcon from '../../assets/datatype-icons/black/list.svg';
import DefinitionEntityDataTypeBlackIcon from '../../assets/datatype-icons/black/definition-entity.svg';
import TextDataTypeBlackIcon from '../../assets/datatype-icons/black/text.svg';
import BooleanDataTypeBlackIcon from '../../assets/datatype-icons/black/boolean.svg';
import UUIDDataTypeBlackIcon from '../../assets/datatype-icons/black/uuid.svg';
import DateTimeDataTypeBlackIcon from '../../assets/datatype-icons/black/date-time.svg';
import UnknownDataTypeBlackIcon from '../../assets/datatype-icons/black/unknown.svg';
import NumberDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/number.svg';
import ListDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/list.svg';
import DefinitionEntityDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/definition-entity.svg';
import TextDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/text.svg';
import BooleanDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/boolean.svg';
import UUIDDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/uuid.svg';
import DateTimeDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/date-time.svg';
import UnknownDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/unknown.svg';
import NumberDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/number.svg';
import ListDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/list.svg';
import DefinitionEntityDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/definition-entity.svg';
import TextDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/text.svg';
import BooleanDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/boolean.svg';
import UUIDDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/uuid.svg';
import DateTimeDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/date-time.svg';
import UnknownDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/unknown.svg';
import DatabaseTableIcon from '../../assets/database-table.png';
import HTTPEndpointIcon from '../../assets/http-endpoint.svg';
import RelationalDatabaseIcon from '../../assets/relational-database.png';
import NullDataTypeBlackIcon from '../../assets/datatype-icons/black/null.svg';
import NullDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/null.svg';
import NullDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/null.svg';
import VoidDataTypeBlackIcon from '../../assets/datatype-icons/black/void.svg';
import VoidDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/void.svg';
import VoidDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/void.svg';
import ExplicitAnyDataTypeBlackIcon from '../../assets/datatype-icons/black/any.svg';
import ExplicitAnyDataTypeDarkGreyIcon from '../../assets/datatype-icons/dark-grey/any.svg';
import ExplicitAnyDataTypeLightGreyIcon from '../../assets/datatype-icons/light-grey/any.svg';
import BytesDataTypeBlackIcon from '../../assets/datatype-icons/black/binary.svg';
import ForeignKeyRefBlackIcon from '../../assets/datatype-icons/black/key-file.svg';
import SQLProgramBlackIcon from '../../assets/datatype-icons/black/sql-program.svg';
import FileDataTypeBlackIcon from '../../assets/datatype-icons/black/file.svg';
import SpreadsheetRangeDataTypeBlackIcon from '../../assets/spreadsheet.svg';
import RGBDataTypeBlackIcon from '../../assets/rgb.svg';
import RGBADataTypeBlackIcon from '../../assets/color-wheel.svg';
import FileDataTypeDarkGreyIcon from '../../assets/datatype-icons/black/file.svg';
import FileDataTypeLightGreyIcon from '../../assets/datatype-icons/black/file.svg';
import CronJobIcon from '../../assets/cron-job.png';
import SQLConnectionConfigIcon from '../../assets/sql-connection-config.svg';
import SSLCertificateConfigIcon from '../../assets/ssl-certificate-config.svg';
import SQLRowTransformerIcon from '../../assets/sql-row-transformer.svg';
import SharedDriveIcon from '../../assets/shared-drive.svg';
import SharedDriveMountIcon from '../../assets/shared-drive-mount.svg';
import ContainerComputeIcon from '../../assets/container-compute.svg';
import CloudFileSystemIcon from '../../assets/cloud-file-system.svg';
import ExternalIntegrationAccountConnectionIcon from '../../assets/authentication.svg';
import ContainerRegistryIcon from '../../assets/container-registry.svg';
import ExecutionIcon from '../../assets/execution.svg';
import TerminalIcon from '../../assets/terminal.svg';
import { ENTITY_FA_ICON_NAMES, LOOP_FA_ICON_NAME } from './ui';
import { getExternalIntegrationIdFromAction } from './utils';
import { envConfig } from '../../config';

export type EntityTypeIconSize = 'small' | 'medium' | 'large';
export type EntityTypeIconColor = 'black' | 'dark-grey' | 'light-grey';

export function resolveBuiltInBaseEntityIcon(
	entity: BuiltInBaseEntityState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	const baseEntity = getFirstBaseEntityWithOwnBase(
		entity,
		BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION
	);

	if (!!baseEntity) {
		if (entity.id === BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION) {
			return { file: ExternalIntegrationAccountConnectionIcon };
		}

		const name = getExternalIntegrationNameFromBuiltInBaseName(
			baseEntity.name
		);

		if (name === 'container-registry') {
			return {
				file: ContainerRegistryIcon
			};
		}

		if (name) {
			const url = `${envConfig.EXTERNAL_INTEGRATIONS_LOGOS_URL}/${name}.svg`;

			return {
				url: url
			};
		}
	}

	switch (entity.name) {
		case BaseEntityNames.HTTP_ENDPOINT:
			return { file: HTTPEndpointIcon };
		case BaseEntityNames.PERSISTED_ENTITY:
			return { file: DatabaseTableIcon };
		case BaseEntityNames.CRON_JOB:
			return { file: CronJobIcon };
		case BaseEntityNames.RELATIONAL_DATABASE:
			return { file: RelationalDatabaseIcon };
		case BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION:
			return { file: ExternalIntegrationAccountConnectionIcon };
		case BaseEntityNames.SQL_ROW_TRANSFORMER:
			return { file: SQLRowTransformerIcon };
		case BaseEntityNames.SQL_ROOT_CONNECTION_CONFIG:
			return { file: SQLConnectionConfigIcon };
		case BaseEntityNames.SQL_SSL_CONNECTION_CONFIG:
			return { file: SSLCertificateConfigIcon };
		case BaseEntityNames.SHARED_DRIVE_ENTITY:
			return { file: SharedDriveIcon };
		case BaseEntityNames.CONTAINER_COMPUTE_ENTITY:
			return { file: ContainerComputeIcon };
		case BaseEntityNames.SHARED_DRIVE_MOUNT_ENTITY:
			return { file: SharedDriveMountIcon };
		case BaseEntityNames.CLOUD_FILE_SYSTEM_BUCKET:
			return { file: CloudFileSystemIcon };
		default:
			return { file: DefinitionEntityDataTypeBlackIcon };
	}
}

export function resolveInfrastructureResourceTypeIcon(resourceType: string): {
	file?: string;
	fa?: string;
} {
	switch (resourceType) {
		case BaseEntityNames.RELATIONAL_DATABASE:
		case 'relational-database':
			return { file: RelationalDatabaseIcon };
		case BaseEntityNames.CLOUD_FILE_SYSTEM_BUCKET:
		case 'cloud-file-system-bucket':
			return { file: CloudFileSystemIcon };
		case BaseEntityNames.HTTP_ENDPOINT:
		case 'http-api':
		case 'http-endpoint':
			return { file: HTTPEndpointIcon };
		case BaseEntityNames.CRON_JOB:
		case 'cron-job':
			return { file: CronJobIcon };
		case 'serverless-function':
			return { file: ExecutionIcon };
		case BaseEntityNames.CONTAINER_COMPUTE_ENTITY:
		case 'container-compute':
			return { file: ContainerComputeIcon };
		case BaseEntityNames.SHARED_DRIVE_ENTITY:
		case 'shared-drive':
			return { file: SharedDriveIcon };
		case 'logs':
			return { file: TerminalIcon };
		default:
			return { file: DefinitionEntityDataTypeBlackIcon };
	}
}

export function resolveComplexEntityIcon(
	entity:
		| DefinitionEntityState
		| BuiltInBaseEntityState
		| PrimitiveEntityState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	switch (entity.type) {
		case EntityType.DefinitionEntity:
			return resolveDefinitionEntityIcon(entity, color);
		case EntityType.BuiltInBaseEntity:
			return resolveBuiltInBaseEntityIcon(entity, color);
		case EntityType.PrimitiveEntity:
			return resolvePrimitiveEntityDataTypeIcon(entity, color);
		default:
			return {
				file: DefinitionEntityDataTypeBlackIcon
			};
	}
}

export function resolveDefinitionEntityIcon(
	entity: DefinitionEntityState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	if (
		checkHasBaseEntity(
			entity,
			BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION
		)
	) {
		const baseEntity = getFirstBaseEntityWithOwnBase(
			entity,
			BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION
		);

		const integrationBuiltInPropertyId =
			BUILT_IN_BASE_ENTITY_IDS['built-in-base-entity'][
				baseEntity.id as 'BUILT_IN_EXTERNAL_INTEGRATION_CONNECTION_ENTITY'
			].properties.integration.id;

		const integrationsProperty = entity.properties.find(
			(property) =>
				property.extends?.id === integrationBuiltInPropertyId ||
				// It could be that this is a specific connection entity, and therefore itself extends the base connection entity
				property.implements.find(
					(impl) => impl.id === integrationBuiltInPropertyId
				)
		);

		if (!!integrationsProperty) {
			const defaultValue = integrationsProperty.getDefaultValue();

			const externalIntegrationId = defaultValue?.value as string | null;

			if (externalIntegrationId === 'container-registry') {
				return {
					file: ContainerRegistryIcon
				};
			}

			if (externalIntegrationId) {
				const url = `${envConfig.EXTERNAL_INTEGRATIONS_LOGOS_URL}/${externalIntegrationId}.svg`;

				return {
					url: url
				};
			}
		}

		return { file: ExternalIntegrationAccountConnectionIcon };
	}

	const baseEntityTypes = getBaseBuiltInEntities(entity);
	if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.HTTP_ENDPOINT
		)
	) {
		return { file: HTTPEndpointIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.PERSISTED_ENTITY
		)
	) {
		return { file: DatabaseTableIcon };
	} else if (
		!!baseEntityTypes.find((ent) => ent.id === BaseEntityNames.CRON_JOB)
	) {
		return { file: CronJobIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.RELATIONAL_DATABASE
		)
	) {
		return { file: RelationalDatabaseIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION
		)
	) {
		return { file: ExternalIntegrationAccountConnectionIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.SQL_ROW_TRANSFORMER
		)
	) {
		return { file: SQLRowTransformerIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.SQL_ROOT_CONNECTION_CONFIG
		)
	) {
		return { file: SQLConnectionConfigIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.SQL_SSL_CONNECTION_CONFIG
		)
	) {
		return { file: SSLCertificateConfigIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.SHARED_DRIVE_ENTITY
		)
	) {
		return { file: SharedDriveIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.CONTAINER_COMPUTE_ENTITY
		)
	) {
		return { file: ContainerComputeIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.SHARED_DRIVE_MOUNT_ENTITY
		)
	) {
		return { file: SharedDriveMountIcon };
	} else if (
		!!baseEntityTypes.find(
			(ent) => ent.id === BaseEntityNames.CLOUD_FILE_SYSTEM_BUCKET
		)
	) {
		return { file: CloudFileSystemIcon };
	}

	return {
		file: DefinitionEntityDataTypeBlackIcon
	};
}

export function resolveLiteralValueDataTypeIcon(
	entity: LiteralValueState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	if (entity.name === PrimitiveTypes.Number) {
		switch (color) {
			case 'black':
				return { file: NumberDataTypeBlackIcon };
			case 'dark-grey':
				return { file: NumberDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: NumberDataTypeLightGreyIcon };
			default:
				return { file: NumberDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.String) {
		switch (color) {
			case 'black':
				return { file: TextDataTypeBlackIcon };
			case 'dark-grey':
				return { file: TextDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: TextDataTypeLightGreyIcon };
			default:
				return { file: TextDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.Boolean) {
		switch (color) {
			case 'black':
				return { file: BooleanDataTypeBlackIcon };
			case 'dark-grey':
				return { file: BooleanDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: BooleanDataTypeLightGreyIcon };
			default:
				return { file: BooleanDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.UUID) {
		switch (color) {
			case 'black':
				return { file: UUIDDataTypeBlackIcon };
			case 'dark-grey':
				return { file: UUIDDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: UUIDDataTypeLightGreyIcon };
			default:
				return { file: UUIDDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.Date) {
		switch (color) {
			case 'black':
				return { file: DateTimeDataTypeBlackIcon };
			case 'dark-grey':
				return { file: DateTimeDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: DateTimeDataTypeLightGreyIcon };
			default:
				return { file: DateTimeDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.KeyValue) {
		switch (color) {
			case 'black':
				return { file: DefinitionEntityDataTypeBlackIcon };
			case 'dark-grey':
				return { file: DefinitionEntityDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: DefinitionEntityDataTypeLightGreyIcon };
			default:
				return { file: DefinitionEntityDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.Untyped) {
		switch (color) {
			case 'black':
				return { file: ExplicitAnyDataTypeBlackIcon };
			case 'dark-grey':
				return { file: ExplicitAnyDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: ExplicitAnyDataTypeLightGreyIcon };
			default:
				return { file: ExplicitAnyDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.Bytes) {
		switch (color) {
			case 'black':
				return { file: BytesDataTypeBlackIcon };
			case 'dark-grey':
				return { file: BytesDataTypeBlackIcon };
			case 'light-grey':
				return { file: BytesDataTypeBlackIcon };
			default:
				return { file: BytesDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.FileReference) {
		switch (color) {
			case 'black':
				return { file: FileDataTypeBlackIcon };
			case 'dark-grey':
				return { file: FileDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: FileDataTypeLightGreyIcon };
			default:
				return { file: FileDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.SpreadsheetRange) {
		switch (color) {
			case 'black':
				return { file: SpreadsheetRangeDataTypeBlackIcon };
			case 'dark-grey':
				return { file: SpreadsheetRangeDataTypeBlackIcon };
			case 'light-grey':
				return { file: SpreadsheetRangeDataTypeBlackIcon };
			default:
				return { file: SpreadsheetRangeDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.RGB) {
		switch (color) {
			case 'black':
				return { file: RGBDataTypeBlackIcon };
			case 'dark-grey':
				return { file: RGBDataTypeBlackIcon };
			case 'light-grey':
				return { file: RGBDataTypeBlackIcon };
			default:
				return { file: RGBDataTypeBlackIcon };
		}
	}

	if (entity.name === PrimitiveTypes.RGBA) {
		switch (color) {
			case 'black':
				return { file: RGBADataTypeBlackIcon };
			case 'dark-grey':
				return { file: RGBADataTypeBlackIcon };
			case 'light-grey':
				return { file: RGBADataTypeBlackIcon };
			default:
				return { file: RGBADataTypeBlackIcon };
		}
	}

	switch (color) {
		case 'black':
			return { file: UnknownDataTypeBlackIcon };
		case 'dark-grey':
			return { file: UnknownDataTypeDarkGreyIcon };
		case 'light-grey':
			return { file: UnknownDataTypeLightGreyIcon };
		default:
			return { file: UnknownDataTypeBlackIcon };
	}
}

export function resolvePrimitiveEntityDataTypeIcon(
	entity: PrimitiveEntityState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	switch (entity.name) {
		case PrimitiveTypes.Number: {
			switch (color) {
				case 'black':
					return { file: NumberDataTypeBlackIcon };
				case 'dark-grey':
					return { file: NumberDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: NumberDataTypeLightGreyIcon };
				default:
					return { file: NumberDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.String:
		case PrimitiveTypes.Enum: {
			switch (color) {
				case 'black':
					return { file: TextDataTypeBlackIcon };
				case 'dark-grey':
					return { file: TextDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: TextDataTypeLightGreyIcon };
				default:
					return { file: TextDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.Boolean: {
			switch (color) {
				case 'black':
					return { file: BooleanDataTypeBlackIcon };
				case 'dark-grey':
					return { file: BooleanDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: BooleanDataTypeLightGreyIcon };
				default:
					return { file: BooleanDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.UUID: {
			switch (color) {
				case 'black':
					return { file: UUIDDataTypeBlackIcon };
				case 'dark-grey':
					return { file: UUIDDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: UUIDDataTypeLightGreyIcon };
				default:
					return { file: UUIDDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.Date: {
			switch (color) {
				case 'black':
					return { file: DateTimeDataTypeBlackIcon };
				case 'dark-grey':
					return { file: DateTimeDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: DateTimeDataTypeLightGreyIcon };
				default:
					return { file: DateTimeDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.Untyped: {
			switch (color) {
				case 'black':
					return { file: ExplicitAnyDataTypeBlackIcon };
				case 'dark-grey':
					return { file: ExplicitAnyDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: ExplicitAnyDataTypeLightGreyIcon };
				default:
					return { file: ExplicitAnyDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.Bytes: {
			switch (color) {
				case 'black':
					return { file: BytesDataTypeBlackIcon };
				case 'dark-grey':
					return { file: BytesDataTypeBlackIcon };
				case 'light-grey':
					return { file: BytesDataTypeBlackIcon };
				default:
					return { file: BytesDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.FileReference: {
			switch (color) {
				case 'black':
					return { file: FileDataTypeBlackIcon };
				case 'dark-grey':
					return { file: FileDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: FileDataTypeLightGreyIcon };
				default:
					return { file: FileDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.SpreadsheetRange: {
			switch (color) {
				case 'black':
					return { file: SpreadsheetRangeDataTypeBlackIcon };
				case 'dark-grey':
					return { file: SpreadsheetRangeDataTypeBlackIcon };
				case 'light-grey':
					return { file: SpreadsheetRangeDataTypeBlackIcon };
				default:
					return { file: SpreadsheetRangeDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.RGB: {
			switch (color) {
				case 'black':
					return { file: RGBDataTypeBlackIcon };
				case 'dark-grey':
					return { file: RGBDataTypeBlackIcon };
				case 'light-grey':
					return { file: RGBDataTypeBlackIcon };
				default:
					return { file: RGBDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.RGBA: {
			switch (color) {
				case 'black':
					return { file: RGBADataTypeBlackIcon };
				case 'dark-grey':
					return { file: RGBADataTypeBlackIcon };
				case 'light-grey':
					return { file: RGBADataTypeBlackIcon };
				default:
					return { file: RGBADataTypeBlackIcon };
			}
		}
		// fa-solid fa-empty-set
		case PrimitiveTypes.Null: {
			switch (color) {
				case 'black':
					return { file: NullDataTypeBlackIcon };
				case 'dark-grey':
					return { file: NullDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: NullDataTypeLightGreyIcon };
				default:
					return { file: NullDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.Void: {
			switch (color) {
				case 'black':
					return { file: VoidDataTypeBlackIcon };
				case 'dark-grey':
					return { file: VoidDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: VoidDataTypeLightGreyIcon };
				default:
					return { file: VoidDataTypeBlackIcon };
			}
		}
		case PrimitiveTypes.SqlProgram: {
			switch (color) {
				case 'black':
					return { file: SQLProgramBlackIcon };
				case 'dark-grey':
					return { file: SQLProgramBlackIcon };
				case 'light-grey':
					return { file: SQLProgramBlackIcon };
				default:
					return { file: SQLProgramBlackIcon };
			}
		}
		case PrimitiveTypes.Untyped: {
			switch (color) {
				case 'black':
					return { file: ExplicitAnyDataTypeBlackIcon };
				case 'dark-grey':
					return { file: ExplicitAnyDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: ExplicitAnyDataTypeLightGreyIcon };
				default:
					return { file: ExplicitAnyDataTypeBlackIcon };
			}
		}
		default: {
			switch (color) {
				case 'black':
					return { file: DefinitionEntityDataTypeBlackIcon };
				case 'dark-grey':
					return { file: DefinitionEntityDataTypeDarkGreyIcon };
				case 'light-grey':
					return { file: DefinitionEntityDataTypeLightGreyIcon };
				default:
					return { file: DefinitionEntityDataTypeBlackIcon };
			}
		}
	}
}

export function resolveExplicitDataTypeIcon(
	icon:
		| PrimitiveTypes
		| 'list'
		| EntityType.DefinitionEntity
		| 'unknown'
		| BaseEntityNames
		| 'foreign-key-ref',
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	if (!icon || icon === 'unknown') {
		switch (color) {
			case 'black':
				return { file: UnknownDataTypeBlackIcon };
			case 'dark-grey':
				return { file: UnknownDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: UnknownDataTypeLightGreyIcon };
			default:
				return { file: UnknownDataTypeBlackIcon };
		}
	}

	if (icon === 'foreign-key-ref') {
		switch (color) {
			case 'black':
				return { file: ForeignKeyRefBlackIcon };
			case 'dark-grey':
				return { file: ForeignKeyRefBlackIcon };
			case 'light-grey':
				return { file: ForeignKeyRefBlackIcon };
			default:
				return { file: ForeignKeyRefBlackIcon };
		}
	}

	// If there is a default value, we can infer the type from it
	if (icon === PrimitiveTypes.Number) {
		switch (color) {
			case 'black':
				return { file: NumberDataTypeBlackIcon };
			case 'dark-grey':
				return { file: NumberDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: NumberDataTypeLightGreyIcon };
			default:
				return { file: NumberDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.String) {
		switch (color) {
			case 'black':
				return { file: TextDataTypeBlackIcon };
			case 'dark-grey':
				return { file: TextDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: TextDataTypeLightGreyIcon };
			default:
				return { file: TextDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.Boolean) {
		switch (color) {
			case 'black':
				return { file: BooleanDataTypeBlackIcon };
			case 'dark-grey':
				return { file: BooleanDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: BooleanDataTypeLightGreyIcon };
			default:
				return { file: BooleanDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.UUID) {
		switch (color) {
			case 'black':
				return { file: UUIDDataTypeBlackIcon };
			case 'dark-grey':
				return { file: UUIDDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: UUIDDataTypeLightGreyIcon };
			default:
				return { file: UUIDDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.Date) {
		switch (color) {
			case 'black':
				return { file: DateTimeDataTypeBlackIcon };
			case 'dark-grey':
				return { file: DateTimeDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: DateTimeDataTypeLightGreyIcon };
			default:
				return { file: DateTimeDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.KeyValue) {
		switch (color) {
			case 'black':
				return { file: DefinitionEntityDataTypeBlackIcon };
			case 'dark-grey':
				return { file: DefinitionEntityDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: DefinitionEntityDataTypeLightGreyIcon };
			default:
				return { file: DefinitionEntityDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.Untyped) {
		switch (color) {
			case 'black':
				return { file: ExplicitAnyDataTypeBlackIcon };
			case 'dark-grey':
				return { file: ExplicitAnyDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: ExplicitAnyDataTypeLightGreyIcon };
			default:
				return { file: ExplicitAnyDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.Null) {
		switch (color) {
			case 'black':
				return { file: NullDataTypeBlackIcon };
			case 'dark-grey':
				return { file: NullDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: NullDataTypeLightGreyIcon };
			default:
				return { file: NullDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.Void) {
		switch (color) {
			case 'black':
				return { file: VoidDataTypeBlackIcon };
			case 'dark-grey':
				return { file: VoidDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: VoidDataTypeLightGreyIcon };
			default:
				return { file: VoidDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.SqlProgram) {
		switch (color) {
			case 'black':
				return { file: SQLProgramBlackIcon };
			case 'dark-grey':
				return { file: SQLProgramBlackIcon };
			case 'light-grey':
				return { file: SQLProgramBlackIcon };
			default:
				return { file: SQLProgramBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.Bytes) {
		switch (color) {
			case 'black':
				return { file: BytesDataTypeBlackIcon };
			case 'dark-grey':
				return { file: BytesDataTypeBlackIcon };
			case 'light-grey':
				return { file: BytesDataTypeBlackIcon };
			default:
				return { file: BytesDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.FileReference) {
		switch (color) {
			case 'black':
				return { file: FileDataTypeBlackIcon };
			case 'dark-grey':
				return { file: FileDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: FileDataTypeLightGreyIcon };
			default:
				return { file: FileDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.SpreadsheetRange) {
		switch (color) {
			case 'black':
				return { file: SpreadsheetRangeDataTypeBlackIcon };
			case 'dark-grey':
				return { file: SpreadsheetRangeDataTypeBlackIcon };
			case 'light-grey':
				return { file: SpreadsheetRangeDataTypeBlackIcon };
			default:
				return { file: SpreadsheetRangeDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.RGB) {
		switch (color) {
			case 'black':
				return { file: RGBDataTypeBlackIcon };
			case 'dark-grey':
				return { file: RGBDataTypeBlackIcon };
			case 'light-grey':
				return { file: RGBDataTypeBlackIcon };
			default:
				return { file: RGBDataTypeBlackIcon };
		}
	}

	if (icon === PrimitiveTypes.RGBA) {
		switch (color) {
			case 'black':
				return { file: RGBADataTypeBlackIcon };
			case 'dark-grey':
				return { file: RGBADataTypeBlackIcon };
			case 'light-grey':
				return { file: RGBADataTypeBlackIcon };
			default:
				return { file: RGBADataTypeBlackIcon };
		}
	}

	if (icon === 'list') {
		switch (color) {
			case 'black':
				return { file: ListDataTypeBlackIcon };
			case 'dark-grey':
				return { file: ListDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: ListDataTypeLightGreyIcon };
			default:
				return { file: ListDataTypeBlackIcon };
		}
	}

	if (icon === EntityType.DefinitionEntity) {
		switch (color) {
			case 'black':
				return { file: DefinitionEntityDataTypeBlackIcon };
			case 'dark-grey':
				return { file: DefinitionEntityDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: DefinitionEntityDataTypeLightGreyIcon };
			default:
				return { file: DefinitionEntityDataTypeBlackIcon };
		}
	}

	if (icon === BaseEntityNames.HTTP_ENDPOINT) {
		return { file: HTTPEndpointIcon };
	} else if (icon === BaseEntityNames.PERSISTED_ENTITY) {
		return { file: DatabaseTableIcon };
	} else if (icon === BaseEntityNames.CRON_JOB) {
		return { file: CronJobIcon };
	} else if (icon === BaseEntityNames.RELATIONAL_DATABASE) {
		return { file: RelationalDatabaseIcon };
	} else if (icon === BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION) {
		return { file: ExternalIntegrationAccountConnectionIcon };
	} else if (icon === BaseEntityNames.SQL_ROW_TRANSFORMER) {
		return { file: SQLRowTransformerIcon };
	} else if (icon === BaseEntityNames.SQL_ROOT_CONNECTION_CONFIG) {
		return { file: SQLConnectionConfigIcon };
	} else if (icon === BaseEntityNames.SQL_SSL_CONNECTION_CONFIG) {
		return { file: SSLCertificateConfigIcon };
	} else if (icon === BaseEntityNames.SHARED_DRIVE_ENTITY) {
		return { file: SharedDriveIcon };
	} else if (icon === BaseEntityNames.CONTAINER_COMPUTE_ENTITY) {
		return { file: ContainerComputeIcon };
	} else if (icon === BaseEntityNames.SHARED_DRIVE_MOUNT_ENTITY) {
		return { file: SharedDriveMountIcon };
	} else if (icon === BaseEntityNames.CLOUD_FILE_SYSTEM_BUCKET) {
		return { file: CloudFileSystemIcon };
	}

	const externalIntegrationId = getExternalIntegrationNameFromBuiltInBaseName(
		icon as BaseEntityNames
	);

	if (externalIntegrationId) {
		if (externalIntegrationId === 'container-registry') {
			return { file: ContainerRegistryIcon };
		}

		const url = `${envConfig.EXTERNAL_INTEGRATIONS_LOGOS_URL}/${externalIntegrationId}.svg`;
		return { url: url };
	}

	return { file: DefinitionEntityDataTypeBlackIcon };
}

export function resolveDataTypeIcon(
	dataType: DataTypeState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	if (!dataType) {
		if (ENTITY_WITH_DEFAULT_VALUE_TYPES.includes(dataType?.parent.type)) {
			const defaultValue = (
				dataType?.parent as EntityWithDefaultValueState
			).getDefaultValue();
			// If there is a default value, we can infer the type from it
			if (defaultValue) {
				return resolveLiteralValueDataTypeIcon(defaultValue, color);
			}
		}

		switch (color) {
			case 'black':
				return { file: UnknownDataTypeBlackIcon };
			case 'dark-grey':
				return { file: UnknownDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: UnknownDataTypeLightGreyIcon };
			default:
				return { file: UnknownDataTypeBlackIcon };
		}
	}

	if (dataType.isList) {
		switch (color) {
			case 'black':
				return { file: ListDataTypeBlackIcon };
			case 'dark-grey':
				return { file: ListDataTypeDarkGreyIcon };
			case 'light-grey':
				return { file: ListDataTypeLightGreyIcon };
			default:
				return { file: ListDataTypeBlackIcon };
		}
	}

	if (!!dataType.foreignKeyRef) {
		switch (color) {
			case 'black':
				return { file: ForeignKeyRefBlackIcon };
			case 'dark-grey':
				return { file: ForeignKeyRefBlackIcon };
			case 'light-grey':
				return { file: ForeignKeyRefBlackIcon };
			default:
				return { file: ForeignKeyRefBlackIcon };
		}
	}

	// if (!dataType.entity) {
	// 	switch (color) {
	// 		case 'black':
	// 			return { file: UnknownDataTypeBlackIcon };
	// 		case 'dark-grey':
	// 			return { file: UnknownDataTypeDarkGreyIcon };
	// 		case 'light-grey':
	// 			return { file: UnknownDataTypeLightGreyIcon };
	// 		default:
	// 			return { file: UnknownDataTypeBlackIcon };
	// 	}
	// }

	if (dataType.entity?.type === EntityType.DefinitionEntity) {
		return resolveDefinitionEntityIcon(dataType.entity);
	} else if (dataType.entity?.type === EntityType.BuiltInBaseEntity) {
		return resolveBuiltInBaseEntityIcon(dataType.entity);
	} else if (dataType.entity?.type === EntityType.PrimitiveEntity) {
		const resolved = resolvePrimitiveEntityDataTypeIcon(
			dataType.entity,
			color
		);
		return resolved;
	} else if (dataType.entity?.type === EntityType.LiteralValue) {
		return resolveLiteralValueDataTypeIcon(dataType.entity, color);
	}

	if (
		!!dataType.andChildrenGroup &&
		Array.isArray(dataType.andChildrenGroup) &&
		dataType.andChildrenGroup.length > 0
	) {
		if (dataType.andChildrenGroup.length === 1) {
			// For AND groups, just resolve the first child's data-type icon
			const firstChild = dataType.andChildrenGroup[0];

			if (!!firstChild) {
				return resolveDataTypeIcon(firstChild, color);
			}
		} else {
			const primitiveChild = dataType.andChildrenGroup.find(
				(child) =>
					!!child?.entity &&
					[
						EntityType.PrimitiveEntity,
						EntityType.LiteralValue
					].includes(child.entity?.type)
			);

			if (!!primitiveChild) {
				return resolveDataTypeIcon(primitiveChild, color);
			}

			const numberOfDefEntities = dataType.andChildrenGroup.filter(
				(child) =>
					[EntityType.DefinitionEntity].includes(child.entity?.type)
			).length;

			const definitionEntityChild = dataType.andChildrenGroup.find(
				(child) =>
					[EntityType.DefinitionEntity].includes(child.entity?.type)
			);

			if (numberOfDefEntities === 1 && definitionEntityChild) {
				return resolveDataTypeIcon(definitionEntityChild, color);
			}

			if (numberOfDefEntities > 1) {
				// If there are multiple definition entities, just return the generic definition entity icon
				switch (color) {
					case 'black':
						return { file: DefinitionEntityDataTypeBlackIcon };
					case 'dark-grey':
						return { file: DefinitionEntityDataTypeDarkGreyIcon };
					case 'light-grey':
						return { file: DefinitionEntityDataTypeLightGreyIcon };
					default:
						return { file: DefinitionEntityDataTypeBlackIcon };
				}
			}
		}
	}

	if (
		!!dataType.orChildrenGroup &&
		Array.isArray(dataType.orChildrenGroup) &&
		dataType.orChildrenGroup.length > 0
	) {
		// const hasNullChild = dataType.orChildrenGroup.find(
		// 	(child) => child.entity?.name === PrimitiveTypes.Null,
		// );

		// For OR groups, just resolve the first child's data-type icon
		const childrenWithoutNull = dataType.orChildrenGroup.filter(
			(child) => child.entity?.name !== PrimitiveTypes.Null
		);

		if (childrenWithoutNull.length === 1) {
			const firstChild = childrenWithoutNull[0];

			if (!!firstChild) {
				return resolveDataTypeIcon(firstChild, color);
			}
		}
	}

	if (ENTITY_WITH_DEFAULT_VALUE_TYPES.includes(dataType?.parent.type)) {
		const defaultValue = (
			dataType?.parent as EntityWithDefaultValueState
		).getDefaultValue();
		// If there is a default value, we can infer the type from it
		if (defaultValue) {
			return resolveLiteralValueDataTypeIcon(defaultValue, color);
		}
	}

	switch (color) {
		case 'black':
			return { file: UnknownDataTypeBlackIcon };
		case 'dark-grey':
			return { file: UnknownDataTypeDarkGreyIcon };
		case 'light-grey':
			return { file: UnknownDataTypeLightGreyIcon };
		default:
			return { file: UnknownDataTypeBlackIcon };
	}
}

export function resolveEntityDataTypeIcon(
	entity:
		| EntityWithValueState
		| PropertyState
		| ValueDescriptorState
		| FunctionDeclarationState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	if (!entity) {
		switch (color) {
			case 'black':
				return {
					file: UnknownDataTypeBlackIcon
				};
			case 'dark-grey':
				return {
					file: UnknownDataTypeDarkGreyIcon
				};
			case 'light-grey':
				return {
					file: UnknownDataTypeLightGreyIcon
				};
			default:
				return {
					file: UnknownDataTypeBlackIcon
				};
		}
	}

	const dataType =
		entity?.getDataType(null) || inferDataType(entity, null) || null;

	return resolveDataTypeIcon(dataType, color);
}

export function resolveEntityIcon(
	entity: EntityState,
	color: EntityTypeIconColor = 'black'
): { fa?: string; file?: string; url?: string } {
	if (!entity) {
		return {
			file: UnknownDataTypeBlackIcon
		};
	}

	switch (entity.type) {
		case EntityType.DefinitionEntity:
			return resolveDefinitionEntityIcon(entity, color);
		case EntityType.BuiltInBaseEntity: {
			return resolveBuiltInBaseEntityIcon(entity, color);
		}
		case EntityType.PrimitiveEntity: {
			return resolvePrimitiveEntityDataTypeIcon(entity, color);
		}
		case EntityType.GlobalEvent: {
			return {
				fa: ENTITY_FA_ICON_NAMES[EntityType.GlobalEvent]
			};
		}
		// case EntityType.FunctionDeclaration
		case EntityType.InternalCall: {
			return {
				fa: ENTITY_FA_ICON_NAMES[EntityType.InternalCall]
			};
		}
		case EntityType.VariableInstance:
		case EntityType.OutputMap:
		case EntityType.InputMap: {
			if (entity.declaration) {
				const declaration = entity.declaration;

				return resolveEntityIcon(declaration, color);
			}

			return resolveEntityDataTypeIcon(entity, color);
		}
		case EntityType.Operation: {
			const externalIntegrationId =
				getExternalIntegrationIdFromAction(entity);

			if (externalIntegrationId) {
				const url = `${envConfig.EXTERNAL_INTEGRATIONS_LOGOS_URL}/${externalIntegrationId}.svg`;

				return {
					url: url
				};
			}

			return {
				fa: ENTITY_FA_ICON_NAMES[EntityType.Operation]
			};
		}
		case EntityType.Property:
		case EntityType.VariableDeclaration:
		case EntityType.ArgumentDeclaration:
		case EntityType.ValueDescriptor: {
			return resolveEntityDataTypeIcon(entity, color);
		}
		case EntityType.Loop: {
			return {
				fa: LOOP_FA_ICON_NAME
			};
		}
		case EntityType.ActionDescriptor: {
			// Here, find out what is describing,
			// If they are a built in value-descriptor, they must be part of one of the types
			const asOperation = entity.project.operationDeclarations.find(
				(op) => op.id === entity.id
			);

			if (asOperation) {
				// If it is an operation that represents an external integration,
				// Return the URL of the icon
				const externalIntegrationId =
					getExternalIntegrationIdFromAction(asOperation);

				if (externalIntegrationId) {
					const url = `${envConfig.EXTERNAL_INTEGRATIONS_LOGOS_URL}/${externalIntegrationId}.svg`;

					return {
						url: url
					};
				}

				return {
					fa: ENTITY_FA_ICON_NAMES[EntityType.Operation]
				};
			}

			const asEvent = entity.project.globalEventActionDescriptors.find(
				(op) => op.id === entity.id
			);

			if (asEvent) {
				return {
					fa: ENTITY_FA_ICON_NAMES[EntityType.GlobalEvent]
				};
			}

			const asLoop = entity.project.loopDeclarations.find(
				(op) => op.id === entity.id
			);

			if (asLoop) {
				return {
					fa: LOOP_FA_ICON_NAME
				};
			}

			const asCondition = entity.project.conditionDeclarations.find(
				(op) => op.id === entity.id
			);

			if (asCondition) {
				return {
					fa: ENTITY_FA_ICON_NAMES[EntityType.Condition]
				};
			}

			// Other, used as global data-types to be used in the project by the user
			return {
				fa: 'fa-cube'
			};
		}
		default: {
			return {
				// @ts-ignore
				fa: ENTITY_FA_ICON_NAMES[entity.type] || 'fa-cube'
			};
		}
	}
}
